import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import Switch from 'src/shared/components/switch/Switch';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from 'src/shared/components/ui/tooltip';
import { GraphApiError } from 'src/shared/state/api-slices/graphApiSlice';
import { useSetProjectPortfolioInclusionMutation } from 'src/app/admin/state/api/adminGraphSlice';

const describeToggleError = (error: unknown): string => {
    const message = (error as GraphApiError)?.failures?.[0]?.message;
    return message && message.trim() ? message : 'Something went wrong. Please try again.';
};

/** Shown instead of a live toggle for a project without Habitat Insights: nothing would ever
 * resolve a pending link, since nothing produces the data the aggregate step waits for. */
const NOT_ELIGIBLE_REASON = "This project doesn't have Habitat Insights enabled, so it can't be added to the portfolio.";

type PortfolioToggleCellProps = {
    projectId: string;
    projectName: string;
    /** Whether the project's definition has the Habitat Insights feature — the only projects the
     * portfolio pipeline (asset backfill → country mapping → HLCA aggregate) can ever resolve. */
    hasHabitatInsights: boolean;
    /** Current membership, from basicProjectList's includeInPortfolio - read on page load so this
     * reflects reality rather than whatever the last person to open this page happened to click. */
    inPortfolio: boolean;
};

/**
 * Ops toggle to add/remove one project from its customer's portfolio (DD-6079) - a single
 * setProjectPortfolioInclusion mutation flipping project_definition's includeInPortfolio flag.
 *
 * Deliberately a plain boolean, not a three-state on/pending/off control: a "pending" state would
 * need staleness-detection infra (has the backfill run since this was toggled?) that doesn't exist
 * yet. On failure the toggle simply doesn't move — it stays driven by the last-known server state
 * rather than an optimistic local flip, so it never visually claims success it didn't earn.
 */
const PortfolioToggleCell = ({ projectId, projectName, hasHabitatInsights, inPortfolio }: PortfolioToggleCellProps) => {
    const [setProjectPortfolioInclusion, { isLoading }] = useSetProjectPortfolioInclusionMutation();
    const [error, setError] = useState<string | null>(null);

    const label = `${inPortfolio ? 'Remove' : 'Add'} ${projectName} ${inPortfolio ? 'from' : 'to'} the portfolio`;

    if (!hasHabitatInsights) {
        return (
            <div className='flex items-center' onClick={event => event.stopPropagation()}>
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            {/* A disabled control still needs a wrapper to catch the hover for its tooltip. */}
                            <span className='inline-flex items-center' data-testid={`portfolio-toggle-disabled-${projectId}`}>
                                <Switch checked={false} disabled onChange={() => {}} label={NOT_ELIGIBLE_REASON} labelPlacement='top' />
                            </span>
                        </TooltipTrigger>
                        <TooltipPortal>
                            <TooltipContent side='left'>{NOT_ELIGIBLE_REASON}</TooltipContent>
                        </TooltipPortal>
                    </Tooltip>
                </TooltipProvider>
            </div>
        );
    }

    const handleToggle = async (checked: boolean) => {
        setError(null);
        try {
            await setProjectPortfolioInclusion({ projectId, includeInPortfolio: checked }).unwrap();
        } catch (err) {
            setError(describeToggleError(err));
        }
    };

    return (
        <div className='flex items-center gap-2' onClick={event => event.stopPropagation()}>
            <span className='inline-flex items-center' data-testid={`portfolio-toggle-${projectId}`}>
                <Switch checked={inPortfolio} onChange={handleToggle} disabled={isLoading} label={label} labelPlacement='top' />
            </span>
            {error && (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className='inline-flex items-center' data-testid={`portfolio-toggle-error-${projectId}`}>
                                <AlertCircle size={16} className='shrink-0 text-destructive' />
                            </span>
                        </TooltipTrigger>
                        <TooltipPortal>
                            <TooltipContent side='left'>{error}</TooltipContent>
                        </TooltipPortal>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
};

export default PortfolioToggleCell;
